
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { DEFAULT_ACCESS_CONFIG } from '@/components/users/role-access/constants';

export type RoleAccessConfig = Record<string, string[]>;

export const useRemoteRoleAccess = () => {
    return useQuery({
        queryKey: ['role-access-configs'],
        queryFn: async (): Promise<RoleAccessConfig> => {
            // Use any for the table name since it's newly created and might not be in the generated types yet
            const { data, error } = await (supabase as any)
                .from('role_access_configs')
                .select('role, allowed_paths');

            if (error) {
                console.error('Error fetching role access configs:', error);
                return DEFAULT_ACCESS_CONFIG;
            }

            if (!data || (data as any[]).length === 0) {
                return DEFAULT_ACCESS_CONFIG;
            }

            // Transform array of rows into Record<role, paths[]>
            const config: RoleAccessConfig = {};
            (data as any[]).forEach(row => {
                config[row.role] = row.allowed_paths;
            });

            return config;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * Helper to check if a specific path is allowed for a role given the access config.
 * Handles both top-level and nested paths.
 */
export const hasPathAccess = (
    allowedPaths: string[] | null | undefined,
    currentPath: string
): boolean => {
    if (!allowedPaths) return false;

    // Dashboard and root are always allowed
    if (currentPath === '/' || currentPath === '/dashboard') return true;

    // Normalize path by removing leading/trailing slashes and getting segments
    const normalizedPath = currentPath.replace(/^\/|\/$/g, '');
    const segments = normalizedPath.split('/');

    // 1. Direct match for complex IDs like 'price-benchmarking-data-entry'
    // We try to match segments joined with hyphens
    // e.g., /price-benchmarking/data-entry -> 'price-benchmarking-data-entry'
    const fullPathId = segments.join('-');
    if (allowedPaths.includes(fullPathId)) return true;

    // 2. Check the primary segment (module level)
    // e.g., /procurements/123 -> 'procurements' -> match 'procurement'
    const primarySegment = segments[0];

    // Special mapping for common singular/plural variations if needed
    // Many IDs in the system are singular (e.g., 'procurement', 'supplier')
    const normalizedSegment = primarySegment.endsWith('s')
        ? primarySegment.slice(0, -1)
        : primarySegment;

    if (allowedPaths.includes(primarySegment) || allowedPaths.includes(normalizedSegment)) {
        return true;
    }

    // Special cases mapping
    if (primarySegment === 'price-benchmarking' && allowedPaths.includes('price-benchmarking')) return true;
    if (primarySegment === 'farms' && allowedPaths.includes('farms')) return true;
    if (primarySegment === 'harvests' && allowedPaths.includes('harvests')) return true;

    return false;
};
